import { HttpClient } from '@opallabs/api'

/**
 * A browser only opens a handful of connections to one host, and every upload
 * running at the same time splits the same upstream bandwidth. Firing a whole
 * batch at once therefore makes each file crawl and none of them finish early.
 * Uploading a few at a time drains the batch sooner and lets the first files
 * land (and start transcoding) while the rest wait their turn.
 */
const MAX_CONCURRENT_UPLOADS = 3

const uploadQueue = []
let activeUploads = 0

const startQueuedUploads = () => {
  while (activeUploads < MAX_CONCURRENT_UPLOADS && uploadQueue.length > 0) {
    const startUpload = uploadQueue.shift()
    activeUploads += 1
    // A slot that is never released would wedge every later upload in the
    // session, so free it whatever the upload does.
    startUpload().finally(() => {
      activeUploads -= 1
      startQueuedUploads()
    })
  }
}

const enqueueUpload = (upload) =>
  new Promise((resolve, reject) => {
    uploadQueue.push(() => upload().then(resolve, reject))
    startQueuedUploads()
  })

const getConfig = (file, onUploadCallback) => {
  // Encode the header as Base64 to support UTF-8 Unicode characters when using XMLHttpRequest.setRequestHeader()
  const encodedFileName = window.btoa(unescape(encodeURIComponent(file.name)))
  return {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-File-Name': encodedFileName || file.name,
    },
    onUploadProgress: (ProgressEvent) => {
      const progressFraction = ProgressEvent.loaded / ProgressEvent.total
      onUploadCallback(progressFraction)
    },
  }
}

const createAsset = ({
  file,
  onUploadStart = () => {},
  onUploadSuccess = () => {},
  onUploadFailure = () => {},
  onUploadProgress = () => {},
}) => {
  onUploadStart(file)
  return enqueueUpload(() =>
    HttpClient()
      .post('/assets/v2', file, getConfig(file, onUploadProgress))
      .then((assetResponse) => onUploadSuccess(assetResponse.data))
      .catch((error) => onUploadFailure(error))
  )
}

const createAssetFromUrl = ({
  url,
  name,
  prefix,
  onUploadStart = () => {},
  onUploadSuccess = () => {},
  onUploadFailure = () => {},
  onUploadProgress = () => {},
}) => {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const date = new Date()
      const mime = blob.type
      const extension = mime.split('/')[1]
      const fileName = name
        ? `${name}.${extension}`
        : `${prefix || ''}${date.getTime()}.${extension}`
      const file = new File([blob], fileName)
      file.attributes = { srcUrl: url }
      return createAsset({
        file,
        onUploadSuccess,
        onUploadStart,
        onUploadFailure,
        onUploadProgress,
      })
    })
    .catch((err) => {
      onUploadFailure(err)
    })
}

export { MAX_CONCURRENT_UPLOADS, createAsset, createAssetFromUrl }
